import { useEffect, useState, useCallback } from "react";
import Seo from "@/components/Seo";
import { api, resolveMedia } from "@/lib/api";
import { PageHero, Container } from "@/components/public/parts";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("All");
  const [idx, setIdx] = useState(null);

  useEffect(() => { api.get("/public/gallery").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const cats = ["All", ...Array.from(new Set((items || []).map((i) => i.category)))];
  const filtered = (items || []).filter((i) => cat === "All" || i.category === cat);

  const open = (i) => setIdx(i);
  const close = () => setIdx(null);
  const nav = useCallback((d) => setIdx((p) => (p === null ? p : (p + d + filtered.length) % filtered.length)), [filtered.length]);

  useEffect(() => {
    const h = (e) => { if (idx === null) return; if (e.key === "ArrowRight") nav(1); if (e.key === "ArrowLeft") nav(-1); if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [idx, nav]);

  return (
    <>
      <Seo title="Gallery | Love For Mankind" description="Explore authentic photos of Love For Mankind community activities, facilities and events across Ohio." path="/gallery" />
      <PageHero eyebrow="Our Community" title="Gallery" sub="Authentic moments of care, connection and community." breadcrumbs={[{ label: "Home", to: "/" }, { label: "Gallery" }]} />
      <Container className="py-16 lg:py-24">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase()}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-accent"}`}>
                  {c === "All" ? "All" : c.charAt(0) + c.slice(1).toLowerCase()}
                </button>
              ))}
              <span className="ml-auto text-sm text-muted-foreground">{filtered.length} photos</span>
            </div>
            {filtered.length === 0 ? <p className="text-center text-muted-foreground py-12">No photos in this category.</p> : (
              <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
                {filtered.map((it, i) => (
                  <button key={it.id} onClick={() => open(i)} data-testid="gallery-item"
                    className="group block w-full overflow-hidden rounded-2xl border border-border break-inside-avoid">
                    <img src={resolveMedia(it.thumbnail || it.url)} alt={it.altText || it.title} loading="lazy"
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }} />
                  </button>
                ))}
              </div>
            )}
            <p className="text-center text-xs text-muted-foreground mt-8">Photos migrated from the official Love For Mankind website. Assets pending license confirmation are marked REVIEW in the Media Library.</p>
          </>
        )}
      </Container>

      <Dialog open={idx !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-transparent border-0 shadow-none">
          {idx !== null && filtered[idx] && (
            <div className="relative" data-testid="lightbox">
              <img src={resolveMedia(filtered[idx].url)} alt={filtered[idx].altText} className="w-full max-h-[80vh] object-contain rounded-xl bg-black" />
              <button onClick={close} className="absolute top-3 right-3 bg-black/60 text-white rounded-full p-2 hover:bg-black" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
              <button onClick={() => nav(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={() => nav(1)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-black" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">{idx + 1} / {filtered.length}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
